import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import { HeadlineInterface } from 'common/types/entities/HeadlineInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { FontUpdate } from '../components/Font/BaseFont'
import HeadingFont from '../components/Font/HeadingFont'
import { useUpdateEntity, useUpdateProp } from '../hooks/useUpdateProps'
import { DelayOption } from '../options'
import DeviceAppearanceNew from '../options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from '../options/HtmlAttrId'
import LetterSpacing from '../styles/LetterSpacing'
import TextAlignStyle from '../styles/TextAlignStyle'
import TextFontSize from '../styles/TextFontSize'
import TextLineHeight from '../styles/TextLineHeight'

type HeadlineSettingsProps = {
  entity: HeadlineInterface
  defaultEntity?: HeadlineInterface
}

function HeadlineSettings({ entity, defaultEntity }: HeadlineSettingsProps) {
  const { isMobile } = useDeviceModeContext()
  const updateEntity = useUpdateEntity<HeadlineInterface>()
  const updateProp = useUpdateProp(entity)
  const fontFamily = isMobile
    ? entity.mobileFontFamily || entity.fontFamily
    : entity.fontFamily
  const fontStyle = isMobile
    ? entity.mobileFontStyle || entity.fontStyle
    : entity.fontStyle
  const fontWeight = isMobile
    ? entity.mobileFontWeight || entity.fontWeight
    : entity.fontWeight

  const globalTextAlign = usePage(pageSelectors.getGlobalHeadingAlign)
  const globalMobileTextAlign = usePage(
    pageSelectors.getGlobalMobileHeadingAlign,
  )

  function handleFontChange(fontUpdate: FontUpdate) {
    if (isMobile) {
      if (fontUpdate.fontFileId) {
        delete entity.mobileFontStyle
        delete entity.mobileFontWeight
        updateEntity({
          ...entity,
          mobileFontFamily: fontUpdate.fontFamily,
          mobileFontFileId: fontUpdate.fontFileId,
        })
      } else {
        if (entity.mobileFontFileId) {
          delete entity.mobileFontFileId
        }
        updateEntity({
          ...entity,
          mobileFontFamily: fontUpdate.fontFamily,
          ...(fontUpdate.fontStyle
            ? { mobileFontStyle: fontUpdate.fontStyle }
            : {}),
          ...(fontUpdate.fontWeight
            ? { mobileFontWeight: fontUpdate.fontWeight }
            : {}),
        })
      }
    } else {
      if (fontUpdate.fontFileId) {
        delete entity.fontStyle
        delete entity.fontWeight
        updateEntity({
          ...entity,
          fontFamily: fontUpdate.fontFamily,
          fontFileId: fontUpdate.fontFileId,
        })
      } else {
        if (entity.fontFileId) {
          delete entity.fontFileId
        }
        updateEntity({
          ...entity,
          fontFamily: fontUpdate.fontFamily,
          ...(fontUpdate.fontStyle ? { fontStyle: fontUpdate.fontStyle } : {}),
          ...(fontUpdate.fontWeight
            ? { fontWeight: fontUpdate.fontWeight }
            : {}),
        })
      }
    }
  }

  return (
    <>
      <GroupTitle
        isFirst
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'fontSize',
                'fontFamily',
                'fontFileId',
                'fontStyle',
                'fontWeight',
                'lineHeight',
                'textAlign',
                'letterSpacing',
              ]}
              mobileKeys={[
                'mobileFontSize',
                'mobileFontFamily',
                'mobileFontFileId',
                'mobileFontWeight',
                'mobileLineHeight',
                'mobileTextAlign',
                'mobileLetterSpacing',
              ]}
            />
          )
        }
      >
        typography
      </GroupTitle>
      <TextFontSize
        update={updateProp('fontSize')}
        mobileUpdate={updateProp('mobileFontSize')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        typographyType={WebsiteSettingsTypographyTypeEnum.Heading}
        removeEntityStyle={updateProp('fontSize')}
        removeEntityMobileStyle={updateProp('mobileFontSize')}
      />
      <TextLineHeight
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        lineHeight={entity.lineHeight}
        typographyType={WebsiteSettingsTypographyTypeEnum.Heading}
        mobileLineHeight={entity.mobileLineHeight}
        removeEntityStyle={updateProp('lineHeight')}
        removeEntityMobileStyle={updateProp('mobileLineHeight')}
      />
      <HeadingFont
        fontFamily={fontFamily}
        fontStyle={fontStyle}
        fontWeight={fontWeight}
        update={handleFontChange}
      />
      <LetterSpacing
        change={updateProp(isMobile ? 'mobileLetterSpacing' : 'letterSpacing')}
        letterSpacing={
          isMobile ? entity.mobileLetterSpacing : entity.letterSpacing
        }
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={['backgroundColor']}
              mobileKeys={['mobileBackgroundColor']}
            />
          )
        }
      >
        color
      </GroupTitle>
      <ColorPickerControl
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        isClearable
        label="entity_settings.text.background_color"
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <TextAlignStyle
        textAlign={entity.textAlign || globalTextAlign}
        mobileTextAlign={
          entity.mobileTextAlign || entity.textAlign || globalMobileTextAlign
        }
        update={updateProp('textAlign')}
        mobileUpdate={updateProp('mobileTextAlign')}
        isResetIconVisible={
          !!(isMobile ? entity.mobileTextAlign : entity.textAlign)
        }
        removeEntityStyle={updateProp('textAlign')}
        removeEntityMobileStyle={updateProp('mobileTextAlign')}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default HeadlineSettings
